import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useAuth, API } from "@/AuthContext";
import { Users, FileText, TrendingUp, Plus, ArrowRight, Download } from "lucide-react";

export default function DashboardHome() {
  const { user, t } = useAuth();
  const [stats, setStats] = useState({ total_patients: 0, total_sessions: 0, sessions_last_7_days: 0 });
  const [sessions, setSessions] = useState([]);
  const [patients, setPatients] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/stats`),
      axios.get(`${API}/sessions`),
      axios.get(`${API}/patients`),
    ]).then(([s, ses, pat]) => {
      setStats(s.data); setSessions(ses.data.slice(0, 5)); setPatients(pat.data);
    }).catch(() => {});
  }, []);

  const patientName = (id) => {
    const p = patients.find((x) => x.patient_id === id);
    return p ? `${p.first_name} ${p.last_name}` : id;
  };

  const downloadPDF = async (e, sessionId) => {
    e.preventDefault();
    e.stopPropagation();
    setDownloadingId(sessionId);
    try {
      const r = await axios.get(`${API}/sessions/${sessionId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `arztbericht_${sessionId}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } finally {
      setDownloadingId(null);
    }
  };

  const kpis = [
    { label: t.dash.kpiPatients, value: stats.total_patients, icon: Users },
    { label: t.dash.kpiSessions, value: stats.total_sessions, icon: FileText },
    { label: t.dash.kpiWeek, value: stats.sessions_last_7_days, icon: TrendingUp },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-pine">{new Date().toLocaleDateString(undefined, { weekday: "long" })}</div>
        <h1 className="mt-2 font-heading text-4xl tracking-tight">{t.dash.welcome} <span className="text-pine">{user?.name?.split(" ")[0]}</span>.</h1>
      </motion.div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        {kpis.map((k, i) => (
          <motion.div key={k.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }}
            className="bg-surface border border-hairline rounded-2xl p-6 hover:shadow-soft transition"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-ink-muted font-semibold">{k.label}</div>
                <div className="mt-3 font-heading text-4xl tracking-tight">{k.value}</div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-pine-50 text-pine flex items-center justify-center">
                <k.icon className="w-5 h-5" strokeWidth={1.75} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Recent + Quick actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-surface border border-hairline rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl tracking-tight">{t.dash.recentSessions}</h2>
            <Link to="/dashboard/sessions/new" className="text-sm text-pine hover:underline" data-testid="recent-newsession-link">{t.dash.newSession} →</Link>
          </div>
          <div className="mt-4 divide-y divide-hairline">
            {sessions.length === 0 && (
              <div className="py-12 text-center text-ink-muted">{t.dash.noSessions}</div>
            )}
            {sessions.map((s) => (
              <Link
                key={s.session_id}
                to={`/dashboard/patients/${s.patient_id}?session=${s.session_id}`}
                className="flex items-center gap-4 py-4 hover:bg-pine-50/40 -mx-2 px-2 rounded-lg transition"
                data-testid={`session-row-${s.session_id}`}
              >
                <div className="w-10 h-10 rounded-xl bg-pine-50 text-pine flex items-center justify-center font-heading">
                  {patientName(s.patient_id)?.[0] || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{patientName(s.patient_id)}</div>
                  <div className="text-sm text-ink-muted truncate">{s.soap?.assessment?.slice(0, 90) || "—"}</div>
                </div>
                <div className="text-xs text-ink-muted whitespace-nowrap hidden sm:block">{new Date(s.created_at).toLocaleDateString()}</div>
                <button
                  onClick={(e) => downloadPDF(e, s.session_id)}
                  disabled={downloadingId === s.session_id}
                  title="Arztbericht (PDF) herunterladen"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-hairline text-xs font-medium hover:bg-pine hover:text-bone hover:border-pine disabled:opacity-50 transition whitespace-nowrap"
                  data-testid={`dashboard-pdf-${s.session_id}`}
                >
                  <Download className="w-3.5 h-3.5" />
                  {downloadingId === s.session_id ? "..." : "Arztbericht"}
                </button>
                <ArrowRight className="w-4 h-4 text-ink-muted" />
              </Link>
            ))}
          </div>
        </div>

        <div className="bg-pine text-bone rounded-2xl p-6">
          <div className="text-xs uppercase tracking-[0.18em] text-bone/70 font-semibold">{t.dash.quickActions}</div>
          <div className="mt-4 space-y-3">
            <Link to="/dashboard/patients?new=1" className="flex items-center justify-between bg-bone/10 hover:bg-bone/15 transition rounded-xl px-4 py-3" data-testid="qa-add-patient">
              <span className="inline-flex items-center gap-2 text-sm font-medium"><Plus className="w-4 h-4" /> {t.dash.addPatient}</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/dashboard/sessions/new" className="flex items-center justify-between bg-bone text-pine hover:bg-bone/90 transition rounded-xl px-4 py-3" data-testid="qa-new-session">
              <span className="inline-flex items-center gap-2 text-sm font-medium"><Plus className="w-4 h-4" /> {t.dash.newSession}</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mt-8 text-xs text-bone/65 leading-relaxed">
            <strong className="text-bone">DSGVO · HIPAA-Ready</strong><br/>
            EU-hosted infrastructure. End-to-end encrypted patient records.
          </div>
        </div>
      </div>
    </div>
  );
}
